import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  accountHubRestSegments,
  isAccountOrderDetailPath,
} from '../AccountShell.js';
import { AccountOrderDetailSheet } from '../components/AccountOrderDetailSheet.js';
import {
  DIRECTORY_DENSITY_STORAGE_KEYS,
  useDirectoryDensity,
} from '../../../shared/ui/directoryDensity.js';
import { DirectoryPageHeader } from '../../../shared/ui/DirectoryPageHeader.js';
import { DirectoryResultsSkeleton } from '../../../shared/ui/DirectoryResults.js';
import { DIRECTORY_PAGE_STACK_NESTED_CLASS } from '../../../shared/ui/directoryPickerLayout.js';

export type AccountDirectoryHubKind = 'orders' | 'shop';

/** G10/G6 — derive hub kind from account rest segments (not pathname.includes). */
export function resolveAccountDirectoryHubKind(
  pathname: string,
): AccountDirectoryHubKind | null {
  const rest = accountHubRestSegments(pathname);
  if (rest === null || rest.length === 0) {
    return null;
  }
  // Orders list hub and order-detail overlay route share the orders directory chrome.
  if (rest[0] === 'orders' && (rest.length === 1 || isAccountOrderDetailPath(pathname))) {
    return 'orders';
  }
  if (rest[0] === 'shop') {
    return 'shop';
  }
  return null;
}

/** Transaction id segment when pathname is `/account/orders/:transactionId` (any). */
export function resolveAccountOrderDetailTransactionId(
  pathname: string,
): string | null {
  if (!isAccountOrderDetailPath(pathname)) {
    return null;
  }
  const rest = accountHubRestSegments(pathname);
  const id = rest?.[1]?.trim() ?? '';
  return id.length > 0 ? id : null;
}

/**
 * Session-gate loading chrome — matches live directory hub pages (header + skeleton props).
 * G6: order-detail URL keeps orders hub skeleton + mounts AccountOrderDetailSheet while validating.
 */
export function AccountDirectoryHubSessionLoading({
  pathname,
}: {
  readonly pathname: string;
}): JSX.Element {
  const { t } = useTranslation('customer');
  const navigate = useNavigate();
  const kind = resolveAccountDirectoryHubKind(pathname);
  const detailTransactionId = resolveAccountOrderDetailTransactionId(pathname);

  const closeOrderDetail = useCallback((): void => {
    void navigate('/account/orders', { replace: true });
  }, [navigate]);

  const orderDetailSheet =
    detailTransactionId !== null ? (
      <AccountOrderDetailSheet
        open
        transactionId={detailTransactionId}
        listReceiptRow={undefined}
        onClose={closeOrderDetail}
      />
    ) : null;

  if (kind === 'shop') {
    return <AccountShopDirectoryHubSessionLoading />;
  }

  return (
    <div className={DIRECTORY_PAGE_STACK_NESTED_CLASS}>
      <DirectoryPageHeader
        title={t('account.orders.title')}
        description={t('account.orders.description')}
        testId="account-orders-header"
      />
      <DirectoryResultsSkeleton
        density="list"
        listLayout="twoCol"
        cardsLayout="twoCol"
        size="lg"
        rowDensity="comfortable"
        testId="account-session-directory-skeleton"
      />
      {orderDetailSheet}
    </div>
  );
}

function AccountShopDirectoryHubSessionLoading(): JSX.Element {
  const { t } = useTranslation('customer');
  const { density } = useDirectoryDensity(DIRECTORY_DENSITY_STORAGE_KEYS.favorites);

  return (
    <div className={DIRECTORY_PAGE_STACK_NESTED_CLASS}>
      <DirectoryPageHeader
        title={t('account.shop.favoritesTitle')}
        description={t('account.shop.favoritesDescription')}
        titleAs="h1"
        testId="customer-favorites-header"
      />
      <DirectoryResultsSkeleton
        density={density}
        listLayout="split2"
        size="lg"
        rowDensity="comfortable"
        testId="account-session-directory-skeleton"
      />
    </div>
  );
}
